import {
  HomepageSlideshowItemElementContentModelContentType,
  type ApiBlockItemModel,
  type HomepageSlideshowItemPropertiesModel,
  type IApiElementModel,
} from '@/services/platform/api/model';
import { ImagesToImageMap } from './image';
import { LinksToLinkMap, type Link } from './links';

export interface HeroSlide {
  id: string;
  image?: string;
  eyebrow?: string;
  headline?: string;
  link?: Link;
  /**
   * Id of the collection picked on the slide, when there is one.
   *
   * Carried through raw rather than resolved here: the picker value the CMS returns is a stub
   * (`properties: {}`), so the collection's own image needs a fetch, which a pure mapper cannot do.
   * `useShopHeroSlides` uses this to build the link synchronously and to hydrate the image.
   */
  collectionId?: string;
}

function extractHeroImageUrl(
  properties: HomepageSlideshowItemPropertiesModel | undefined
): string | undefined {
  if (!properties) return undefined;
  // mediaMobile first — desktop media as fallback; ImagesToImageMap prefixes relative paths
  const img = ImagesToImageMap(properties.mediaMobile) ?? ImagesToImageMap(properties.media);
  if (img?.src) return img.src;
  return properties.mediaUrlMobile ?? properties.mediaUrl;
}

export function mapCmsHeroSlides(items: ApiBlockItemModel[] | undefined): HeroSlide[] {
  if (!items) return [];
  const slides: HeroSlide[] = [];
  for (const block of items) {
    const element = block.content as IApiElementModel | undefined;
    if (!element) continue;
    if (element.contentType !== HomepageSlideshowItemElementContentModelContentType.homepageSlideshowItem) {
      continue;
    }
    const properties = (element as unknown as { properties?: HomepageSlideshowItemPropertiesModel })
      .properties;
    slides.push({
      id: element.id,
      image: extractHeroImageUrl(properties),
      eyebrow: properties?.title,
      headline: properties?.subtitle ?? properties?.label,
      link: LinksToLinkMap(properties?.link ?? undefined),
      collectionId: properties?.collectionPicker?.[0]?.id,
    });
  }
  return slides;
}
